import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import DashboardLayout from '../layouts/DashboardLayout';
import { listRecordings, updateRecording } from '../api/recordings';

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString();
};

const AdminRecordingsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [showArchived, setShowArchived] = useState(false);

  const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (query) params.q = query;
      if (showArchived) params.includeArchived = true;
      const data = await listRecordings(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number.isFinite(data?.total) ? data.total : 0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load recordings');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, pageSize, query, showArchived]);

  const handlePublish = async (row, publish) => {
    try {
      await updateRecording(row._id, { recordingPublished: publish });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update recording');
    }
  };

  const handleArchive = async (row, archive) => {
    try {
      await updateRecording(row._id, { recordingArchived: archive });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update recording');
    }
  };

  const columns = useMemo(() => ([
    {
      field: 'title',
      headerName: 'Session',
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => params?.row?.title || params?.row?.subject || 'Session',
    },
    {
      field: 'educator',
      headerName: 'Educator',
      minWidth: 200,
      valueGetter: (params) => {
        const educator = params?.row?.educator;
        return educator ? `${educator.name || ''} ${educator.surname || ''}`.trim() : '-';
      },
    },
    {
      field: 'startTime',
      headerName: 'Start',
      minWidth: 190,
      valueGetter: (params) => formatDateTime(params?.row?.startTime),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {params?.row?.recordingPublished ? (
            <Chip size="small" color="success" label="Published" />
          ) : (
            <Chip size="small" color="warning" label="Unpublished" />
          )}
          {params?.row?.recordingArchived && (
            <Chip size="small" color="default" label="Archived" />
          )}
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayCircleOutlineRoundedIcon />}
            disabled={!params?.row?.recordingUrl}
            onClick={() => params?.row?.recordingUrl && window.open(params.row.recordingUrl, '_blank', 'noopener,noreferrer')}
          >
            Watch
          </Button>
          <IconButton
            size="small"
            color={params?.row?.recordingPublished ? 'success' : 'primary'}
            onClick={() => handlePublish(params.row, !params?.row?.recordingPublished)}
          >
            <PublishRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color={params?.row?.recordingArchived ? 'warning' : 'default'}
            onClick={() => handleArchive(params.row, !params?.row?.recordingArchived)}
          >
            <ArchiveRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ]), []);

  return (
    <DashboardLayout title="Recordings Access">
      <Box>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Search by title"
              value={query}
              onChange={(e) => {
                setPage(1);
                setQuery(e.target.value);
              }}
              sx={{ minWidth: 240 }}
            />
            <TextField
              select
              label="Archived"
              value={showArchived ? 'yes' : 'no'}
              onChange={(e) => {
                setPage(1);
                setShowArchived(e.target.value === 'yes');
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="no">Hide archived</MenuItem>
              <MenuItem value="yes">Show archived</MenuItem>
            </TextField>
            <Button startIcon={<RefreshRoundedIcon />} onClick={load}>
              Refresh
            </Button>
          </Stack>

          <Box sx={{ height: 560 }}>
            <DataGrid
              rows={items}
              getRowId={(row) => row._id}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                color: 'text.primary',
                bgcolor: 'background.paper',
                '& .MuiDataGrid-virtualScroller': { bgcolor: 'background.paper' },
                '& .MuiDataGrid-row': { bgcolor: 'background.paper' },
                '& .MuiDataGrid-cell': { color: 'text.primary' },
                '& .MuiDataGrid-cellContent': { color: 'text.primary' },
                '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', color: 'text.primary' },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: 'text.primary' },
                '& .MuiDataGrid-footerContainer': { bgcolor: 'grey.50', color: 'text.primary' },
                '& .MuiDataGrid-overlay': { color: 'text.secondary' },
              }}
              initialState={{ pagination: { paginationModel: { pageSize } } }}
              pageSizeOptions={[10, 20, 50, 100]}
              hideFooterPagination
            />
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Showing {items.length} of {total}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Pagination
                count={pages}
                page={Math.min(page, pages)}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="small"
              />
              <TextField
                select
                label="Rows per page"
                value={pageSize}
                onChange={(event) => {
                  setPage(1);
                  setPageSize(parseInt(event.target.value, 10));
                }}
                size="small"
                sx={{ minWidth: 120 }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} / page
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </DashboardLayout>
  );
};

export default AdminRecordingsPage;
